import { brandColors } from 'brandColors';
import _ from 'lodash';
import { DateTime, Interval } from 'luxon';
import { ContainerHistoryEvent } from 'services/clients/watchList';
import { Terminal } from 'types/Shipping';
import { CurrentContainerStatus } from 'types/Watchlist';
import { formatDate } from 'utils/common';
import { centsToDollars, FormattedValue, toUppercase } from 'utils/container-transforms';

export const formatEventStatus = (
  event: ContainerHistoryEvent
): FormattedValue<{ tooltip?: string; colors: { background: string; text: string } }> => {
  switch (event.eventSubtype) {
    case CurrentContainerStatus.GatedOut:
      return { value: 'Gated out', meta: { colors: { background: '#D6E4EE', text: '#3D2651' } } };
    case CurrentContainerStatus.Terminated:
      return {
        value: 'Terminated',
        meta: { colors: { background: brandColors.colors.gray[900], text: '#5B5E6A' } },
      };
    case CurrentContainerStatus.OnVessel:
      return { value: 'On vessel', meta: { colors: { background: '#F5DFCC', text: '#3D2D1E' } } };
    case CurrentContainerStatus.Manifested:
      return { value: 'Manifested', meta: { colors: { background: '#DEECDC', text: '#363D23' } } };
    case CurrentContainerStatus.AtTerminal:
      return { value: 'At terminal', meta: { colors: { background: '#E6DEED', text: '#46112C' } } };
    default:
      return { value: toUppercase(event.eventSubtype || 'Unknown') };
  }
};

interface Change {
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transform: (value: any, event: ContainerHistoryEvent) => string | null | undefined;
}

const boolTransform = (value: boolean) => (value ? 'Yes' : 'No');
const holdTransform = (value: string) => (value === 'unknown' ? null : _.startCase(value));
const appointmentTransform = (appointmentStartTime: string, event: ContainerHistoryEvent) => {
  const start = DateTime.fromISO(appointmentStartTime);
  const end = event.originalEvent.pickupAppointment.endTime
    ? DateTime.fromISO(event.originalEvent.pickupAppointment.endTime)
    : undefined;

  if (end) {
    const interval = Interval.fromDateTimes(start, end);
    return `${start.toFormat('EEE, MMM d')} ${interval.toFormat('hh:mm')} ${start.toFormat(
      'ZZZZ'
    )}`;
  }
  return start.toFormat('EEE, MMM d hh:mm ZZZZ');
};

// Based on the event timezone, this function returns a mapping of the event changes
// to the human readable label and a transform function to format the column values
export const getMapping = (event: ContainerHistoryEvent) => {
  const changeMapping: { [key: string]: Change } = {
    lastFreeDay: {
      label: 'Last Free Day',
      transform: (value: string) => formatDate(value, 'ddd, MMM d', event.timezone),
    },
    goodThroughDate: {
      label: 'Good Through Date',
      transform: (value: string) => formatDate(value, 'ddd, MMM d', event.timezone),
    },
    appointmentStartDate: {
      label: 'Appointment Scheduled',
      transform: appointmentTransform,
    },
    available: {
      label: 'Available for Pickup?',
      transform: boolTransform,
    },
    demurrageDue: {
      label: 'Demurrage Due',
      transform: boolTransform,
    },
    demurrageFeeUsCents: {
      label: 'Demurrage Amount',
      transform: (value: number) => `${centsToDollars(value)}`,
    },
    terminal: {
      label: 'Pickup Location',
      transform: (value: Terminal) => value.name,
    },
    customsStatusLabel: {
      label: 'Customs Hold',
      transform: holdTransform,
    },
    freightStatusLabel: {
      label: 'Freight Hold',
      transform: holdTransform,
    },
    shippingLineStatusLabel: {
      label: 'Shipping Line Hold',
      transform: holdTransform,
    },
    ctfStatusLabel: {
      label: 'CTF Hold',
      transform: holdTransform,
    },
    tmfStatusLabel: {
      label: 'TMF Hold',
      transform: holdTransform,
    },
    holdsStatusLabel: {
      label: 'Holds',
      transform: holdTransform,
    },
    terminalStatusLabel: {
      label: 'Terminal Hold',
      transform: holdTransform,
    },
    location: {
      label: 'Yard Location',
      transform: (value: string) => value,
    },
    closedArea: {
      label: 'Closed Area',
      transform: boolTransform,
    },
  };

  return changeMapping;
};

// Wrapper function to check for null or undefined before calling the transform functions
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function safetyCheck(oldFunction: (...args: any) => string | null | undefined) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (...args: any) => {
    if (args[0] === null || args[0] === undefined) {
      return null;
    }
    try {
      return oldFunction(...args);
    } catch (e) {
      // Prevents the app from crashing if the transform function throws an error
      // from a bad input
      // eslint-disable-next-line no-console
      console.error('Safety error:', e);
      return null;
    }
  };
}
